import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Briefcase, DollarSign, Clock, CheckSquare } from 'lucide-react';
import { serviceAPI } from '../services/api';
import { useI18n } from '../contexts/I18nContext';

interface Service {
  id: string;
  name: string;
  description: string;
  service_type: string;
  estimated_duration: number;
  base_price: number;
  rooms: string[];
  is_active: boolean;
  created_at: string;
}

interface RoomOption {
  id: string;
  name: string;
  display_name: string;
}

const SERVICE_TYPES = [
  { value: 'complete', label: 'Limpeza Completa', color: '#10b981' },
  { value: 'partial', label: 'Limpeza Parcial', color: '#3b82f6' },
  { value: 'deep', label: 'Limpeza Profunda', color: '#8b5cf6' },
  { value: 'maintain', label: 'Manutenção', color: '#f59e0b' }
];

const ROOM_OPTIONS: RoomOption[] = [
  { id: 'living_room', name: 'living_room', display_name: 'Sala de Estar' },
  { id: 'kitchen', name: 'kitchen', display_name: 'Cozinha' },
  { id: 'bedroom', name: 'bedroom', display_name: 'Quarto' },
  { id: 'bathroom', name: 'bathroom', display_name: 'Banheiro' },
  { id: 'dining_room', name: 'dining_room', display_name: 'Sala de Jantar' },
  { id: 'laundry', name: 'laundry', display_name: 'Lavanderia' },
  { id: 'garage', name: 'garage', display_name: 'Garagem' },
  { id: 'balcony', name: 'balcony', display_name: 'Varanda' },
  { id: 'office', name: 'office', display_name: 'Escritório' },
  { id: 'hallway', name: 'hallway', display_name: 'Corredor' },
  { id: 'basement', name: 'basement', display_name: 'Porão/Basement' },
  { id: 'stove', name: 'stove', display_name: 'Fogão' },
  { id: 'refrigerator', name: 'refrigerator', display_name: 'Geladeira' },
  { id: 'laundry_wash', name: 'laundry_wash', display_name: 'Lavar Roupa' },
  { id: 'ironing', name: 'ironing', display_name: 'Passar Roupa' },
  { id: 'windows', name: 'windows', display_name: 'Janelas' }
];

const Servicos: React.FC = () => {
  const { t, formatCurrency } = useI18n();
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deletingService, setDeletingService] = useState<Service | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null); // Por padrão, mostrar todos
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    service_type: 'complete',
    estimated_duration: 60,
    base_price: 0,
    rooms: [] as string[]
  });

  const loadServices = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('service_type', filterType);
      if (filterActive !== null) params.append('is_active', filterActive.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/services?${queryString}` : '/services';
      const response = await serviceAPI.get(url);
      setServices(response.data.services || []);
    } catch (error) {
      alert(t('services.error.loading') || 'Erro ao carregar serviços');
    }
    setIsLoading(false);
  };

  const createService = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Validar e garantir que os valores numéricos são válidos
      const payload = {
        ...formData,
        estimated_duration: isNaN(formData.estimated_duration) ? 15 : Math.max(15, formData.estimated_duration),
        base_price: isNaN(formData.base_price) ? 0 : Math.max(0, formData.base_price)
      };
      
      await serviceAPI.post('/services', payload);
      setShowForm(false);
      resetForm();
      await loadServices();
      alert(t('success') || 'Serviço criado com sucesso!');
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao criar serviço';
      alert(errorMsg);
    }
    setIsLoading(false);
  };

  const updateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingService) return;
    
    setIsLoading(true);
    try {
      // Validar e garantir que os valores numéricos são válidos
      const payload = {
        ...formData,
        estimated_duration: isNaN(formData.estimated_duration) ? 15 : Math.max(15, formData.estimated_duration),
        base_price: isNaN(formData.base_price) ? 0 : Math.max(0, formData.base_price)
      };
      
      await serviceAPI.put(`/services/${editingService.id}`, payload);
      setEditingService(null);
      resetForm();
      await loadServices();
      alert(t('success') || 'Serviço atualizado com sucesso!');
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || t('error') || 'Erro ao atualizar serviço';
      alert(errorMsg);
    }
    setIsLoading(false);
  };

  const openDeleteModal = (service: Service) => {
    setDeletingService(service);
    setShowDeleteModal(true);
  };

  const softDeleteService = async () => {
    if (!deletingService) return;
    
    setIsLoading(true);
    try {
      // Soft delete: apenas desativa
      await serviceAPI.put(`/services/${deletingService.id}`, { is_active: false });
      
      setShowDeleteModal(false);
      setDeletingService(null);
      
      // Limpar estado antes de recarregar
      setServices([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadServices();
      alert('Serviço desativado com sucesso! Você pode reativá-lo a qualquer momento.');
    } catch (error) {
      console.error('Erro ao desativar serviço:', error);
      alert(t('services.errorDeactivate'));
      await loadServices();
    }
    setIsLoading(false);
  };

  const hardDeleteService = async () => {
    if (!deletingService) return;
    
    setIsLoading(true);
    try {
      // Hard delete: remove permanentemente
      await serviceAPI.delete(`/services/hard/${deletingService.id}`);
      
      setShowDeleteModal(false);
      setDeletingService(null);
      
      // Limpar estado antes de recarregar
      setServices([]);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadServices();
      alert('Serviço deletado permanentemente com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar serviço:', error);
      // Se o backend não suporta hard delete, fazer soft delete
      alert(t('services.hardDeleteUnavailable'));
      await softDeleteService();
    }
    setIsLoading(false);
  };

  const reactivateService = async (serviceId: string) => {
    if (!confirm('Tem certeza que deseja reativar este serviço?')) return;
    
    try {
      await serviceAPI.put(`/services/${serviceId}`, { is_active: true });
      
      // Limpar estado antes de recarregar para forçar atualização
      setServices([]);
      
      // Aguardar um momento para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await loadServices();
      alert('Serviço reativado com sucesso!');
    } catch (error) {
      console.error('Erro ao reativar serviço:', error);
      alert(t('services.errorReactivate'));
      // Recarregar mesmo em caso de erro para sincronizar estado
      await loadServices();
    }
  };

  const startEdit = (service: Service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      service_type: service.service_type,
      estimated_duration: service.estimated_duration,
      base_price: service.base_price,
      rooms: service.rooms || []
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      service_type: 'complete',
      estimated_duration: 60,
      base_price: 0,
      rooms: []
    });
    setEditingService(null);
    setShowForm(false);
  };

  const toggleRoom = (roomId: string) => {
    setFormData(prev => ({
      ...prev,
      rooms: prev.rooms.includes(roomId)
        ? prev.rooms.filter(r => r !== roomId)
        : [...prev.rooms, roomId]
    }));
  };

  const toggleAllRooms = () => {
    if (formData.rooms.length === ROOM_OPTIONS.length) {
      setFormData(prev => ({ ...prev, rooms: [] }));
    } else {
      setFormData(prev => ({ ...prev, rooms: ROOM_OPTIONS.map(r => r.id) }));
    }
  };

  useEffect(() => {
    loadServices();
  }, [filterType, filterActive]);

  const getServiceTypeLabel = (type: string) => {
    const found = SERVICE_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getServiceTypeColor = (type: string) => {
    const found = SERVICE_TYPES.find(t => t.value === type);
    return found ? found.color : '#6b7280';
  };

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>
          {t('services.title') || 'Gestão de Serviços'}
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          {t('services.subtitle') || 'Gerencie os serviços de limpeza oferecidos'}
        </p>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px'
      }}>
        {/* Filtros */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              {t('services.filter.type') || 'Tipo de Serviço'}
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('services.filter.allTypes') || 'Todos os tipos'}</option>
              {SERVICE_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              {t('services.filter.status') || 'Status'}
            </label>
            <select
              value={filterActive === null ? '' : filterActive.toString()}
              onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="">{t('services.filter.allStatus') || 'Todos'}</option>
              <option value="true">{t('services.filter.active') || 'Ativos'}</option>
              <option value="false">{t('services.filter.inactive') || 'Inativos'}</option>
            </select>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, color: '#333' }}>
            {t('services.list') || 'Lista de Serviços'} ({services.length})
          </h3>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500'
            }}
          >
            <Plus size={20} />
            {t('services.new') || 'Novo Serviço'}
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('services.loading') || 'Carregando serviços...'}</div>
          </div>
        ) : services.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <Briefcase size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <div>{t('services.notFound') || 'Nenhum serviço encontrado'}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {services.map((service) => (
              <div key={service.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: '#fafafa',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>{service.name}</h3>
                    <span style={{
                      backgroundColor: service.is_active ? '#d1fae5' : '#fee2e2',
                      color: service.is_active ? '#065f46' : '#991b1b',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}>
                      {service.is_active ? (t('active') || 'Ativo') : (t('inactive') || 'Inativo')}
                    </span>
                  </div>
                  
                  <span style={{
                    backgroundColor: getServiceTypeColor(service.service_type) + '20',
                    color: getServiceTypeColor(service.service_type),
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'inline-block'
                  }}>
                    {getServiceTypeLabel(service.service_type)}
                  </span>
                </div>

                {service.description && (
                  <p style={{ 
                    color: '#666', 
                    fontSize: '14px', 
                    margin: '0 0 15px 0',
                    lineHeight: '1.5'
                  }}>
                    {service.description}
                  </p>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DollarSign size={16} style={{ color: '#10b981' }} />
                    <span style={{ fontWeight: '600', color: '#10b981', fontSize: '18px' }}>
                      {formatCurrency(service.base_price)}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666' }}>
                    <Clock size={16} />
                    <span style={{ fontSize: '14px' }}>
                      {service.estimated_duration} {t('services.minutes') || 'minutos'}
                    </span>
                  </div>

                  {service.rooms && service.rooms.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                        <CheckSquare size={16} style={{ color: '#8b5cf6' }} />
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#666' }}>
                          {t('services.includedRooms') || 'Cômodos'}: {service.rooms.length}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginLeft: '24px' }}>
                        {service.rooms.slice(0, 3).map(room => {
                          const roomOption = ROOM_OPTIONS.find(r => r.id === room || r.name === room);
                          return (
                            <span key={room} style={{
                              backgroundColor: '#f3f4f6',
                              padding: '2px 8px',
                              borderRadius: '3px',
                              fontSize: '11px',
                              color: '#555'
                            }}>
                              {roomOption?.display_name || room}
                            </span>
                          );
                        })}
                        {service.rooms.length > 3 && (
                          <span style={{ fontSize: '11px', color: '#999' }}>
                            +{service.rooms.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', paddingTop: '15px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    onClick={() => startEdit(service)}
                    style={{
                      flex: 1,
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <Edit size={14} />
                    {t('edit') || 'Editar'}
                  </button>
                  {service.is_active ? (
                    <button
                      onClick={() => openDeleteModal(service)}
                      style={{
                        flex: 1,
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <Trash2 size={14} />
                      {t('delete') || 'Deletar'}
                    </button>
                  ) : (
                    <button
                      onClick={() => reactivateService(service.id)}
                      style={{
                        flex: 1,
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      <Plus size={14} />
                      Reativar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
              {editingService 
                ? (t('services.edit') || 'Editar Serviço') 
                : (t('services.new') || 'Novo Serviço')}
            </h2>

            <form onSubmit={editingService ? updateService : createService}>
              {/* Nome */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('services.name') || 'Nome do Serviço'} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Limpeza Completa - Apartamento 2 Quartos"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Descrição */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('services.description') || 'Descrição'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder={t('services.descriptionPlaceholder')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Tipo de Serviço */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  {t('services.type') || 'Tipo de Serviço'} *
                </label>
                <select
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  {SERVICE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Duração e Preço */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('services.duration') || 'Duração (min)'} *
                  </label>
                  <input
                    type="number"
                    value={formData.estimated_duration}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 15 : parseInt(e.target.value);
                      setFormData({ ...formData, estimated_duration: isNaN(value) ? 15 : value });
                    }}
                    required
                    min="15"
                    step="15"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    {t('services.price') || 'Preço Base'} *
                  </label>
                  <input
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                      setFormData({ ...formData, base_price: isNaN(value) ? 0 : value });
                    }}
                    required
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              {/* Seleção de Cômodos */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontWeight: '500' }}>
                    {t('services.rooms') || 'Cômodos Incluídos'} ({formData.rooms.length})
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllRooms}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #8b5cf6',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      color: '#8b5cf6',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    {formData.rooms.length === ROOM_OPTIONS.length 
                      ? (t('deselectAll') || 'Desmarcar Todos')
                      : (t('selectAll') || 'Marcar Todos')}
                  </button>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: '10px',
                  padding: '15px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  {ROOM_OPTIONS.map(room => (
                    <label
                      key={room.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: formData.rooms.includes(room.id) ? '#ede9fe' : 'transparent',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.rooms.includes(room.id)}
                        onChange={() => toggleRoom(room.id)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px' }}>{room.display_name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    color: '#333'
                  }}
                >
                  {t('cancel') || 'Cancelar'}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {isLoading 
                    ? (t('saving') || 'Salvando...') 
                    : (t('save') || 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Delete */}
      {showDeleteModal && deletingService && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>{t('services.deleteTitle')}</h3>
            
            <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
              <strong>Serviço:</strong> {deletingService.name}
            </p>
            
            <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>{t('services.deleteChoice')}</p>

            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '10px',
              marginBottom: '20px'
            }}>
              {/* Opção 1: Desativar (Soft Delete) */}
              <button
                onClick={softDeleteService}
                disabled={isLoading}
                style={{
                  padding: '15px',
                  border: '2px solid #f59e0b',
                  borderRadius: '6px',
                  backgroundColor: '#fffbeb',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fef3c7';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fffbeb';
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#92400e' }}>
                  ⚠️ Desativar (Recomendado)
                </div>
                <div style={{ fontSize: '13px', color: '#78350f' }}>
                  O serviço ficará oculto mas pode ser reativado a qualquer momento mudando o filtro de status para "Inativos".
                </div>
              </button>

              {/* Opção 2: Deletar Permanentemente (Hard Delete) */}
              <button
                onClick={hardDeleteService}
                disabled={isLoading}
                style={{
                  padding: '15px',
                  border: '2px solid #ef4444',
                  borderRadius: '6px',
                  backgroundColor: '#fef2f2',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  opacity: isLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fee2e2';
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) e.currentTarget.style.backgroundColor = '#fef2f2';
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#991b1b' }}>
                  🗑️ Deletar Permanentemente
                </div>
                <div style={{ fontSize: '13px', color: '#7f1d1d' }}>
                  <strong>{t('products.warning')}</strong> O serviço será removido permanentemente do banco de dados e NÃO poderá ser recuperado!
                </div>
              </button>
            </div>

            {/* Botão Cancelar */}
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingService(null);
              }}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isLoading ? 0.6 : 1
              }}
            >{t('actions.cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Servicos;


